import _extends from "@babel/runtime/helpers/extends";
import { Box, Button, Heading } from '@wix/design-system';
import { DomainSearchSuiteModule, SearchRecommendationLayout } from '@wix/domains-search-suite';
import React from 'react';
import { DomainSearchGateModule } from '../../module/domainSearchGate.module';
import s from '../common.scss';
import styles from './PreSuggestions.scss';
import { PRE_SUGGESTIONS_FALLBACK_QUERY, usePreSuggestions } from '../../hooks/usePreSuggestions';
import { LazyRecommendationLabel } from '../LazyRecommedationLabel/LazyRecommendationLabel';
import { useIsHarmonyTheme, useIsMobile } from '@wix/core-domain-search';
const SearchRecommendationSharedProps = {
  direction: 'vertical',
  minHeight: '183px',
  height: '100%',
  align: 'space-between',
  gap: '46px',
  padding: '20px',
  borderRadius: '12px',
  border: '1px solid #DEDEDE'
};
const containerPropsFor = perception => perception ? {
  ...SearchRecommendationSharedProps,
  gap: '24px',
  boxSizing: 'border-box'
} : SearchRecommendationSharedProps;
const harmonyCardProps = {
  border: 'none',
  backgroundColor: 'var(--wds-color-fill-surface-raised, #FFFFFF)',
  boxShadow: 'var(--wds-card-base-shadow, none)'
};
const domainInfoPropsFor = perception => perception ? {
  direction: 'vertical',
  gap: '8px'
} : {
  direction: 'vertical',
  gap: 'SP2',
  margin: '8px 0'
};
const SearchRecommendationListContainer = _ref => {
  let {
    children,
    ...props
  } = _ref;
  const isMobile = useIsMobile();
  return /*#__PURE__*/React.createElement(Box, _extends({
    direction: isMobile ? 'vertical' : 'horizontal',
    display: "flex",
    width: "100%",
    flex: 1,
    gap: "SP4",
    align: "space-between",
    flexWrap: "wrap"
  }, props), children);
};
const preSuggestionLayoutFor = perception => {
  const containerProps = containerPropsFor(perception);
  const domainInfoProps = domainInfoPropsFor(perception);
  const titleClassName = perception ? `${styles.domainTitle} ${styles.domainTitlePerception}` : styles.domainTitle;
  return {
    SearchRecommendationListContainer: props => /*#__PURE__*/React.createElement(SearchRecommendationListContainer, props),
    SearchRecommendationContainer: _ref2 => {
      let {
        children,
        ...props
      } = _ref2;
      const isMobile = useIsMobile();
      const isHarmonyTheme = useIsHarmonyTheme();
      return /*#__PURE__*/React.createElement(Box, _extends({}, containerProps, isMobile && {
        minHeight: 'auto',
        gap: '16px'
      }, props, isHarmonyTheme && harmonyCardProps), children);
    },
    SearchRecommendationSkeletonContainer: _ref3 => {
      let {
        children,
        ...props
      } = _ref3;
      const isHarmonyTheme = useIsHarmonyTheme();
      return /*#__PURE__*/React.createElement(Box, _extends({}, containerProps, props, isHarmonyTheme && harmonyCardProps), children);
    },
    DomainInfoContainer: _ref4 => {
      let {
        children,
        ...props
      } = _ref4;
      return /*#__PURE__*/React.createElement(Box, _extends({}, domainInfoProps, props), children);
    },
    Skeleton: () => /*#__PURE__*/React.createElement(SearchRecommendationLayout.Skeleton, {
      limit: 3
    }),
    GetItButtonContainer: _ref5 => {
      let {
        children,
        ...props
      } = _ref5;
      return /*#__PURE__*/React.createElement(Box, _extends({
        flex: 1,
        align: "right"
      }, props), children);
    },
    GetItButton: _ref6 => {
      let {
        children,
        ...props
      } = _ref6;
      return /*#__PURE__*/React.createElement(Button, _extends({
        size: "medium",
        skin: "dark"
      }, props, {
        priority: "primary"
      }), children);
    },
    TitleDisplay: _ref7 => {
      let {
        children,
        ...props
      } = _ref7;
      return /*#__PURE__*/React.createElement(Heading, {
        size: "large",
        className: titleClassName
      }, props.title);
    },
    Divider: () => /*#__PURE__*/React.createElement(React.Fragment, null),
    HoverBox: _ref8 => {
      let {
        children,
        ...props
      } = _ref8;
      return /*#__PURE__*/React.createElement(Box, _extends({}, props, {
        flex: 1,
        className: ""
      }), children);
    },
    ActionsContainer: _ref9 => {
      let {
        children,
        ...props
      } = _ref9;
      return /*#__PURE__*/React.createElement(Box, _extends({
        gap: "SP4",
        verticalAlign: "middle",
        align: "space-between"
      }, props), children);
    },
    PricingWrapper: _ref10 => {
      let {
        children,
        ...props
      } = _ref10;
      return /*#__PURE__*/React.createElement(Box, _extends({}, props, {
        align: "left",
        className: styles.pricing
      }), children);
    }
  };
};
export const PreSuggestionLayout = preSuggestionLayoutFor(false);
const PreSuggestionPerceptionLayout = preSuggestionLayoutFor(true);
export const PreSuggestions = _ref11 => {
  let {
    fallbackQuery = PRE_SUGGESTIONS_FALLBACK_QUERY,
    perception = false
  } = _ref11;
  const {
    recommendations,
    isLoading,
    isPricingLoading,
    matchLabels,
    isLoadingLabels,
    disabled,
    hidden,
    onGetItClick
  } = usePreSuggestions(fallbackQuery);
  const {
    t
  } = DomainSearchGateModule.internals.useTranslations();
  if (hidden) {
    return null;
  }
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP7",
    width: "100%",
    align: "center",
    padding: "SP4 0 SP10 0"
  }, /*#__PURE__*/React.createElement(Heading, {
    size: "large",
    className: s.sectionTitle
  }, t('domain_search_gate.domain_gate.suggestions.header')), /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.SearchRecommendationList, {
    items: recommendations,
    isLoading: isLoading,
    isPricingLoading: isPricingLoading,
    Layouts: {
      ...(perception ? PreSuggestionPerceptionLayout : PreSuggestionLayout),
      LabelContainer: LazyRecommendationLabel({
        isLoading: isLoadingLabels,
        matchLabels
      })
    },
    disabled: disabled,
    onClickGetIt: onGetItClick,
    firstSuggestionPrimary: false
  }));
};
//# sourceMappingURL=PreSuggestions.js.map